'use client';
import { Fragment, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { Product } from 'shared/types/product';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import AddToCart from '@/components/customercenter-ui/svgs/addToCart';
import { renderProductUrl } from '@/components/customercenter-ui/utils/formatUtils';
import StarFill from '@/customercenter-ui/icons/startFill';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { filterOnSaleItem } from '@/helpers/utils/AlgoliaInsights';
import { clearanceTextClass } from '@/helpers/utils/brandButton';
import { formatAmount, getSelectedSite } from '@/helpers/utils/siteUtils';
import ContentStackImage from '../../../common/contentStackImage/contentStackImage';
import { Button } from '../../ui/button';
interface ProductItemProps {
  product?: any;
  upSell?: boolean;
}

const ProductItem = (props: ProductItemProps) => {
  const { product } = props;
  const router = useRouter();
  const SWATCHES_VISIBLE = 4;
  const [colorActiveButton, setColorActiveButton] = useState(0);
  const [highlightedProductImage, setHighlightedProductImage] = useState(product?.mainImageURL);
  const projectSettings: ProjectSettings = useProjSettings();
  const siteId = getSelectedSite() ?? 'ZBSite';
  const [productUrl, setProductURL] = useState(
    renderProductUrl(`/product/${product?.pdpSeo}/${product?.key}${props?.upSell ? `?upSell=${props.upSell}` : ''}`),
  );

  const handleClick = (swatch: Product, index: number) => {
    setHighlightedProductImage(swatch?.mainImageURL);
    setProductURL(
      renderProductUrl(`/product/${swatch?.pdpSeo}/${swatch?.key}${props?.upSell ? `?upSell=${props.upSell}` : ''}`),
    );
    setColorActiveButton(index);
  };
  return (
    <section className="pb-14">
      <Link href={productUrl}>
        <ContentStackImage
          src={highlightedProductImage ?? ''}
          alt={product?.displayName ?? ''}
          imageWidth={213}
          imageHeight={213}
          className="w-full shrink-0"
        />
      </Link>
      <section>
        <section className="overflow-x-auto lg:overflow-visible lg:pb-0">
          <ul className="flex  w-max  items-center justify-start gap-2">
            <section className="flex h-10 items-center gap-2">
              {product?.swatches?.slice(0, SWATCHES_VISIBLE)?.map((swatch: Product, index: number) => (
                <li key={`tabImage ${index}`}>
                  <button
                    onClick={() => handleClick(swatch, index)}
                    aria-label="choose color button"
                    key={index}
                    className={`flex h-9 w-9 items-center justify-center rounded-full border p-[2px] 
                   ${colorActiveButton === index ? 'border-[2px] border-black' : 'border-[#B6B6B8]'}`}
                  >
                    <ContentStackImage
                      key={index}
                      src={swatch?.swatchImageURL ?? ''}
                      alt={product?.displayName ?? ''}
                      imageWidth={32}
                      imageHeight={32}
                      className="w-full shrink-0 rounded-full"
                    />
                  </button>
                </li>
              ))}
            </section>

            <li>
              <Listbox>
                <div className="relative ">
                  <div>
                    {product?.swatches && product?.swatches?.length > SWATCHES_VISIBLE && (
                      <ListboxButton className="relative w-full cursor-pointer bg-white  pr-5  text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm">
                        <span className="mt-2 block truncate text-sm leading-[19.6px] text-black">{`+${
                          product?.swatches?.length - SWATCHES_VISIBLE
                        }`}</span>
                      </ListboxButton>
                    )}
                  </div>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ListboxOptions className="lg absolute -right-4 top-10 z-10 mt-1  w-[163px]  rounded-md bg-white text-base ring-1 ring-black/5 focus:outline-none sm:text-sm md:right-0">
                      <section className="relative rounded-md border border-foggyGray  bg-white py-3 shadow-md">
                        <div className="absolute right-4 top-0 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 border-l border-t border-foggyGray bg-white"></div>
                        <section className="customtablescrollbar max-h-[180px] overflow-y-auto ">
                          {product?.swatches?.map((swatch: Product, index: number) => (
                            <ListboxOption
                              key={`tabImage ${index}`}
                              className={({ active }) =>
                                `relative cursor-default select-none text-sm leading-[19.6px] text-black ${
                                  active ? 'foggyGray text-black' : 'text-black'
                                }`
                              }
                              value={swatch?.color}
                            >
                              {({}) => (
                                <>
                                  <button
                                    onClick={() => handleClick(swatch, index)}
                                    aria-label="choose color button"
                                    key={index}
                                    className="flex w-full items-center gap-2 px-4 py-[5px]"
                                  >
                                    <ContentStackImage
                                      src={swatch?.swatchImageURL ?? ''}
                                      alt={`${product?.displayName} ${swatch?.color}`}
                                      imageWidth={28}
                                      imageHeight={28}
                                      className={`flex  h-8 w-8 shrink-0 items-center justify-center rounded-full border-[2px] 
                                         ${
                                           colorActiveButton === index
                                             ? 'border-[2px] border-black'
                                             : 'border-foggyGray'
                                         }`}
                                    />
                                    <span>{swatch?.color}</span>
                                  </button>
                                </>
                              )}
                            </ListboxOption>
                          ))}
                        </section>
                      </section>
                    </ListboxOptions>
                  </Transition>
                </div>
              </Listbox>
            </li>
          </ul>
        </section>
        <p className="mt-3 line-clamp-2 h-10 text-sm font-normal leading-[19.6px] text-black">{product?.displayName}</p>
        {product?.minListPrice !== undefined ? (
          <p className="mt-3 h-6 break-words text-[16px] font-normal leading-[19.6px] text-[#666666]">
            {(filterOnSaleItem(product, projectSettings) || product?.onDiscount || product?.onNewListPrice) &&
            product?.minSalePrice ? (
              <>
                <strong
                  className={`break-words pr-2 text-base font-bold leading-[20.8px] ${clearanceTextClass(siteId)}`}
                >
                  {product?.maxSalePrice && product?.minSalePrice && product?.maxSalePrice > product?.minSalePrice ? (
                    <>
                      {formatAmount(product?.minSalePrice ?? '')} - {formatAmount(product?.maxSalePrice ?? '')}
                    </>
                  ) : (
                    <>{formatAmount(product?.minSalePrice ?? '')}</>
                  )}
                </strong>
                <em className="break-words not-italic line-through">
                  {product?.maxListPrice && product?.maxListPrice > product?.minListPrice ? (
                    <>
                      {formatAmount(product?.minListPrice ?? '')} - {formatAmount(product?.maxListPrice ?? '')}
                    </>
                  ) : (
                    <>{formatAmount(product?.minListPrice ?? '')}</>
                  )}
                </em>
              </>
            ) : (
              <span>
                {product?.maxListPrice && product?.maxListPrice > product?.minListPrice ? (
                  <>
                    {formatAmount(product?.minListPrice ?? '')} - {formatAmount(product?.maxListPrice ?? '')}
                  </>
                ) : (
                  <>{formatAmount(product?.minListPrice ?? '')}</>
                )}
              </span>
            )}
          </p>
        ) : product?.wasPrice ? (
          <p className="break-words text-[16px] font-normal leading-[19.6px] text-[#666666]">
            <span> {<>{formatAmount(product?.wasPrice ?? '')}</>}</span>
          </p>
        ) : (
          <></>
        )}
        {product?.pricePerMonth && (
          <p className="mt-1 h-6 text-lg font-bold leading-[23.4px] text-black">
            {formatAmount(product?.pricePerMonth ?? '')}/month*
          </p>
        )}
        <section className="my-3 flex h-6 items-center gap-1">
          {product?.ratingCount && (
            <section className="flex items-center gap-1">
              <StarFill value={Number(product?.averageRating)} count={5} productId={product?.objectID} />(
              {product?.ratingCount ?? 0})
            </section>
          )}
        </section>
        <Button
          className="mt-4 flex h-11 w-[150px] items-center gap-2 rounded-md border border-[#B6B6B8] px-4 shadow-sm"
          type="button"
          onClick={() => router.push(productUrl)}
        >
          <AddToCart className="h-4 w-4" />
          Add To Cart
        </Button>
      </section>
    </section>
  );
};

export default ProductItem;
